import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { fromJS, Set as ISet } from 'immutable';
import { LoadSpinner } from 'cstar-react-primitives';

import { newItemIsPlastic, newItemIsSelfBuy } from '../item/newItemForm';
import { saveNewItemAction } from '../item/itemModule';
import NotFound from '../error/NotFound';
import { logError } from '../utils/errorUtils';

import Redirect from './Redirect';
import { push } from './routing';
import { getFlowConfig, getPathStepConfig } from './flowSelectors';
import { getBasePath, getNextPath, isUYOStep } from './flow';
import { redirectIfDataRequirementsUnsatisfied } from './stepUtils';
import { changeStep } from './flowModule';
import { uyoSelectStepCompleted } from '../uyo/select/uyoSelectStepSelectors';
import { newItemIsAccessoriesStepForSelfBuy } from '../accessories/accessoriesUtils';
import { updateFlowProgress } from './flowProgressModule';



export class AsyncFlowStep extends React.Component {
    constructor(props) {
        super(props);

        const { component } = this.props;

        this.state = {
            Component: React.lazy(component)
        };

        this.getChildProps = this.getChildProps.bind(this);
        this.getSubmitAction = this.getSubmitAction.bind(this);
        this.handleStepChange = this.handleStepChange.bind(this);
    }

    componentDidMount() {
        const { loadNextStep } = this.props;

        if (loadNextStep) {
            
            
            
            loadNextStep();
        }

        
        
        this.handleStepChange();
    }

    componentDidUpdate(prevProps) {
        const {
            isReadyToRender,
            isAllowedToRender,
            match
        } = this.props;

        
        
        if (isReadyToRender !== prevProps.isReadyToRender
                || isAllowedToRender !== prevProps.isAllowedToRender
                || match.url !== prevProps.match.url) {
            this.handleStepChange();
        }
    }

    
    getChildProps() {
        const {
            component, path, flowProps, isReady, canLoad, onExit,
            isReadyToRender, isAllowedToRender, redirectPath, dispatch, loadNextStep,
            ...childProps
        } = this.props;

        return childProps;
    }

    
    getSubmitAction() {
        const {
            submitPath,
            onExit,
            dispatch
        } = this.props;

        return (values) => {
            onExit(dispatch, this.getChildProps(), values);
            if (values) {
                try {
                    dispatch(updateFlowProgress(fromJS(values).keySeq()));
                
                } catch (err) {
                    
                    
                    
                }
            }

            if (submitPath === null) {
                
                
                dispatch(saveNewItemAction(values));
            } else {
                push({ pathname: submitPath });
            }
        };
    }

    handleStepChange() {
        const {
            path,
            dispatch,
            isReadyToRender,
            isAllowedToRender,
            location,
            match
        } = this.props;

        
        const payloadSessionID = location.state ? location.state.sessionID : null;

        if (isReadyToRender && isAllowedToRender) {
            
            const stepPath = path.replace(/\/:(\w+)/gi, (regexMatch, p1) => `/${match.params[p1]}`);

            dispatch(changeStep(stepPath, payloadSessionID));
        }
    }

    render() {
        const { Component } = this.state;
        const {
            path, isReadyToRender, isAllowedToRender, redirectPath, flowProgressUpdates, dispatch
        } = this.props;

        if (isReadyToRender) {
            if (isAllowedToRender) {
                return <Component {...this.getChildProps()} submitAction={this.getSubmitAction()} />;
            }

            if (!isAllowedToRender) {
                if (!redirectPath) {
                    
                    logError(`step for path /store/${path} missing redirectPath when not allowed to render!`);
                    return <NotFound />;
                }

                if (flowProgressUpdates) {
                    dispatch(updateFlowProgress(flowProgressUpdates));
                }
                return <Redirect pathname={redirectPath} />;
            }
        }

        return <LoadSpinner />;
    }
}

AsyncFlowStep.defaultProps = {
    flow: null,
    submitPath: null,
    isReadyToRender: false,
    isAllowedToRender: false,
    redirectPath: null,
    dispatch: () => {},
    loadNextStep: null,
    flowProgressUpdates: null
};

AsyncFlowStep.propTypes = {
    
    component: PropTypes.func.isRequired,
    flow: PropTypes.string,
    path: PropTypes.string.isRequired,
    submitPath: PropTypes.string,
    location: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    
    flowProps: PropTypes.func.isRequired,
    isReady: PropTypes.func.isRequired,
    canLoad: PropTypes.func.isRequired,
    onExit: PropTypes.func.isRequired,
    
    isReadyToRender: PropTypes.bool,
    isAllowedToRender: PropTypes.bool,
    redirectPath: PropTypes.string,
    loadNextStep: PropTypes.func,
    flowProgressUpdates: PropTypes.instanceOf(ISet),
    
    dispatch: PropTypes.func
};

export const mapStateToProps = (state, ownProps) => {
    const {
        flow,
        path,
        flowProps,
        isReady,
        canLoad
    } = ownProps;

    const isSelfBuy = newItemIsSelfBuy(state);
    const isPlastic = newItemIsPlastic(state);
    const isUYO = uyoSelectStepCompleted(state) || isUYOStep(path);
    const hasAccessories = newItemIsAccessoriesStepForSelfBuy(state);

    let nextPath;
    let submitPath;
    let loadNextStep;
    if (flow) {
        const flowConfig = getFlowConfig(state).get(flow);
        const branchFlags = {
            isSelfBuy,
            isPlastic,
            isUYO,
            hasAccessories
        };
        nextPath = getNextPath(flowConfig, path, branchFlags);

        if (nextPath) {
            submitPath = `/${getBasePath()}/${nextPath}`;

            
            loadNextStep = getPathStepConfig(state, nextPath).get('component');
        }
    }

    const commonFlowProps = {
        isSelfBuy,
        isPlastic,
        submitPath
    };

    
    const propsFromFlow = flowProps(state, {
        ...commonFlowProps,
        ...ownProps
    });

    const mergedProps = {
        ...ownProps,
        ...commonFlowProps,
        ...propsFromFlow
    };

    
    const isReadyToRender = isReady(state, mergedProps);

    
    let isAllowedToRender = false;
    let redirectPath = null;
    let flowProgressUpdates = null;
    if (isReadyToRender) {
        const { isAllowedOrPath, flowProgress } = canLoad(state, mergedProps);
        flowProgressUpdates = flowProgress;

        if (flow) {
            redirectPath = redirectIfDataRequirementsUnsatisfied(state, flow, path, mergedProps);
        }

        if (redirectPath === null) {
            if (typeof isAllowedOrPath === 'string' || typeof isAllowedOrPath === 'undefined') {
                redirectPath = isAllowedOrPath;
            } else {
                isAllowedToRender = true;
            }
        }
    }
    return {
        ...mergedProps,
        isReadyToRender,
        isAllowedToRender,
        redirectPath,
        flowProgressUpdates,
        loadNextStep
    };
};

export default connect(mapStateToProps)(AsyncFlowStep);
